import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search as SearchIcon,
  MapPin,
  Star,
  Clock,
  Video,
  Home,
  Building,
  Filter,
  ArrowLeft,
  Activity,
  Loader2,
  Calendar,
  ChevronRight,
  User,
  BadgeCheck,
} from 'lucide-react';
import { apiRequest, getInitials } from '../lib/utils';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { ShareProfileDialog } from '../components/ShareProfileDialog';
import { DOCTOR_SPECIALIZATIONS, NURSE_SPECIALIZATIONS } from '../constants/specializations';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

const doctorSpecializations = [
  { value: 'all', label: 'All Specializations' },
  ...DOCTOR_SPECIALIZATIONS,
];

const nurseSpecializations = [
  { value: 'all', label: 'All Specializations' },
  ...NURSE_SPECIALIZATIONS,
];

const serviceTypes = [
  { value: 'all', label: 'All Services' },
  // { value: 'online', label: 'Online Consultation' },
  { value: 'home_visit', label: 'Home Visit' },
  { value: 'clinic', label: 'Clinic Visit' },
];

// Rich SEO content that boosts each landing page above Soft 404 threshold.
// Rendered below the results grid for /doctors, /nurses, /home-visit, /book-appointment.
const SEO_CONTENT = {
  '/doctors': {
    intro: 'Physign helps you find and book appointments with verified doctors across India — all with genuine credentials, transparent fees, and real patient reviews. Whether you need a general physician for a routine check-up, a cardiologist for heart concerns, a dermatologist for skin issues, a pediatrician for your child, a dentist, ENT specialist, orthopedic surgeon, gynecologist, or any other specialist, our platform connects you to qualified professionals near you within seconds. Every doctor listed on Physign undergoes a strict verification process — we check their MCI/state medical council registration, degree certificates, government ID and clinical experience before their profile goes live. This means when you book on Physign, you can trust that the professional you meet is genuine, licensed, and reviewed.',
    body: 'You can filter by specialization, location, gender, consultation fee, home-visit availability, and available time slots. Booking takes under 60 seconds — pick your doctor, choose a slot, confirm, and get instant WhatsApp/SMS confirmation. Consultations can happen at the doctor\'s clinic or as a home visit, depending on availability. Most doctors on Physign have transparent pricing displayed upfront: no hidden fees, no surprises. If you need help choosing the right specialist, our care team is available on WhatsApp to guide you.',
    specializations: ['Cardiologist', 'Dermatologist', 'Pediatrician', 'Gynecologist', 'ENT Specialist', 'Orthopedic', 'General Physician', 'Dentist', 'Neurologist', 'Psychiatrist', 'Urologist', 'Ophthalmologist'],
    faqs: [
      { q: 'How do I book a doctor appointment on Physign?', a: 'Search for a doctor by specialization or location, pick a doctor whose profile and reviews you like, choose an available time slot, confirm your details, and receive instant confirmation on WhatsApp and SMS.' },
      { q: 'Are the doctors on Physign verified?', a: 'Yes. Every doctor is manually verified — we check their medical council registration, degree certificate, government ID, and clinical experience before their profile goes live.' },
      { q: 'Can I book a home visit doctor?', a: 'Yes. Many doctors on Physign offer home visits. Use the "Home Visit" filter to see only doctors available for in-home consultations. Home visit fees are shown upfront.' },
      { q: 'What is the consultation fee range?', a: 'Fees vary by specialization and location. General physicians typically charge ₹300-₹800 for clinic visits and ₹500-₹1500 for home visits. Specialists may charge more.' },
      { q: 'Can I cancel or reschedule my appointment?', a: 'Yes. You can cancel or reschedule from your patient dashboard up to 2 hours before the scheduled time, free of charge.' },
    ],
  },
  '/nurses': {
    intro: 'Physign home nurses provide compassionate, professional care in the comfort of your home. Whether you need short-term help after a surgery, long-term elderly care for an ageing parent, ICU-level care post-discharge, newborn and maternity care for a new mother, or specialized wound care, our vetted nurses are trained to handle it. Every nurse on Physign holds a valid GNM, BSc Nursing or ANM certification, and has hands-on hospital experience before joining our network. We also verify government ID, address, and past employment references — because when someone is caring for your loved one at home, trust is everything.',
    body: 'Book by shift: 8-hour, 12-hour, or 24-hour bookings are available across major Indian cities. Long-term monthly nursing packages are discounted. You can choose the gender of the nurse, language spoken, and specific care skills required. Nurses on Physign are trained in vital-signs monitoring, medication administration, injections, catheter and Ryle\'s tube care, wound dressing, physiotherapy support, elderly mobility assistance, and post-operative care. Our care coordinators help you match with the right nurse in under 2 hours. Every booking comes with an SOP checklist that families can use to track care progress — daily vitals log, medication schedule, dietary intake, physio milestones, and mood observations — all shared with the treating doctor when needed. For elderly patients or dementia care, our nurses are additionally trained in fall-prevention protocols, gentle communication, memory-support routines, and companionship activities that improve quality of life. Cancellation and replacement are free within the first 24 hours if you\'re not satisfied — no questions asked. Physign is trusted by families in Delhi, Mumbai, Bangalore, Chennai, Hyderabad, Pune, Gurugram, Noida, Kolkata, and 15+ more Indian cities.',
    specializations: ['Elderly Care', 'ICU Care at Home', 'Post-Surgery Care', 'Maternity & Newborn Care', 'Wound Care', 'Bed-Ridden Patient Care', 'Injection & Vaccination', 'Catheter Care', 'Physiotherapy Support', 'Dementia & Alzheimer\'s Care'],
    faqs: [
      { q: 'What qualifications do Physign nurses have?', a: 'All nurses hold GNM, BSc Nursing or ANM certifications and have hospital experience. We verify certificates, government ID, and references before onboarding.' },
      { q: 'Can I book a nurse for 24 hours?', a: 'Yes. We offer 8-hour, 12-hour, and 24-hour shifts. Monthly packages come with discounted rates.' },
      { q: 'Can I choose a female nurse?', a: 'Yes. You can filter by gender, language, and specialization when booking.' },
      { q: 'What tasks can Physign nurses perform at home?', a: 'Vitals monitoring, medication, injections, wound dressing, catheter/Ryle\'s tube care, elderly mobility, post-op care, and physiotherapy support.' },
      { q: 'How quickly can a nurse start?', a: 'For most cities, a nurse can start within 2-4 hours of booking. Same-day starts are usually possible.' },
    ],
  },
  '/home-visit': {
    intro: 'Home visit healthcare on Physign brings verified doctors, nurses, and physiotherapists directly to your doorstep — because sometimes going to a clinic isn\'t practical. Elderly parents, bed-ridden patients, new mothers, post-surgery recovery, chronic condition management, or simply the convenience of skipping the hospital queue — home visits solve all of these. All our home visit professionals arrive with proper ID, are punctual within a 30-minute window, and follow strict hygiene protocols. You get the same quality of care as a clinic visit, without the wait or travel.',
    body: 'Home visits are available for general physician consultations, cardiologist reviews for chronic patients, physiotherapy sessions, injections and vaccinations, wound dressing, sample collection for lab tests, IV drips, catheterisation, and elderly care. Prices are transparent and shown upfront on each provider\'s profile. Same-day bookings are usually possible if slots are available. Book online, get a WhatsApp confirmation with the professional\'s name, photo, and estimated arrival time.',
    specializations: ['General Physician Home Visit', 'Home Nurse (24hr)', 'Elderly Care Home Visit', 'Physiotherapy at Home', 'Injection & IV at Home', 'Post-Op Care at Home', 'Newborn Care at Home'],
    faqs: [
      { q: 'How much does a doctor home visit cost?', a: 'Typically ₹500-₹2000 depending on specialization and city. Fees are shown upfront on each doctor\'s profile.' },
      { q: 'Do home visit doctors bring equipment?', a: 'Yes. Doctors carry standard diagnostic equipment (stethoscope, BP monitor, thermometer, glucometer, pulse oximeter). For advanced tests' },
      { q: 'How long does it take for the doctor to arrive?', a: 'Most home visits within a metro city happen within 60-90 minutes of booking. Same-day slots are prioritized.' },
      { q: 'Are home visit doctors verified?', a: 'Yes. Same strict verification as clinic doctors — medical council registration, degree, ID and experience checks.' },
      { q: 'Can I book a home visit for a family member?', a: 'Yes. You can book on someone else\'s behalf — just fill in their name, age, and address at booking.' },
    ],
  },
  '/book-appointment': {
    intro: 'Book a doctor, nurse, or home healthcare appointment on Physign in under 60 seconds. Physign is India\'s trusted health-provider marketplace — every professional listed is verified, reviewed and available across major cities including Delhi, Noida, Bangalore, Chennai, Hyderabad, Pune, Gurugram, Noida, Kolkata, and dozens more. Whether it\'s an urgent same-day appointment, a routine check-up, a home visit, or a long-term care plan, we help you find the right professional and confirm the booking instantly with WhatsApp and SMS notifications.',
    body: 'Physign supports appointment types across doctors (all specializations), home nurses (short-term and long-term), physiotherapy, and lab sample collection. You can filter by location, price, gender, language, and availability. Payments are transparent — you see the fee upfront before you book. There are no hidden charges. If you need help, our care team is on WhatsApp to guide you through choosing the right provider for your needs.',
    specializations: ['General Physician', 'Home Nurse', 'Physiotherapist', 'Cardiologist', 'Pediatrician', 'Dermatologist', 'Gynecologist', 'ENT', 'Dentist', 'Orthopedic'],
    faqs: [
      { q: 'How do I book an appointment on Physign?', a: 'Search for the service you need, filter by location and preferences, pick a provider, choose a slot, and confirm. You\'ll get WhatsApp/SMS confirmation instantly.' },
      { q: 'Is booking free?', a: 'Yes, using Physign to book is free. You only pay the provider\'s displayed fee at the time of consultation (or through the platform if online payment is offered).' },
      { q: 'Can I book same-day appointments?', a: 'Yes. Many providers offer same-day slots. Look for the "Available today" badge on their card.' },
      { q: 'What if I need to cancel?', a: 'You can cancel up to 2 hours before the appointment from your patient dashboard, free of charge.' },
      { q: 'Is my personal information safe?', a: 'Yes. Personal contact details are never shown publicly. Providers only see the info you share during booking.' },
    ],
  },
};

const POPULAR_CITIES = ['Delhi', 'Gurugram', 'Noida', 'Mumbai', 'Bangalore', 'Chennai', 'Hyderabad', 'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Lucknow'];

// Pathname → filter overrides + SEO metadata
const PATH_CONFIG = {
  '/doctors': {
    filters: { role: 'doctor', service: 'all' },
    title: 'Find Doctors Near You — Book Verified Doctors',
    description: 'Search & book appointments with verified doctors across India. Home visit, clinic visit — all specializations covered. Book online in 60 seconds.',
    heading: 'Find Doctors Near You',
    subheading: 'Verified doctors across all specializations',
  },
  '/nurses': {
    filters: { role: 'nurse', service: 'all' },
    title: 'Book Home Nurses — Trained Caregivers at Home',
    description: 'Hire trained home nurses for elderly care, ICU care, post-surgery care, maternity care and more. Book verified caregivers in 60 seconds.',
    heading: 'Book Home Nurses',
    subheading: 'Trained caregivers for elderly, ICU, post-surgery & more',
  },
  '/home-visit': {
    filters: { role: 'all', service: 'home_visit' },
    title: 'Home Visit Doctors & Nurses — Book at Home',
    description: 'Doctors and nurses that come to your home. Book home visits for consultation, injections, elderly care, physiotherapy and more across India.',
    heading: 'Home Visit Doctors & Nurses',
    subheading: 'Verified healthcare professionals — right at your doorstep',
  },
  '/book-appointment': {
    filters: { role: 'all', service: 'all' },
    title: 'Book Doctor Appointment Online — Verified Providers',
    description: 'Book appointments with doctors and nurses across India in 60 seconds. Clinic visits, home visits — all specializations. Trusted by patients.',
    heading: 'Book an Appointment',
    subheading: 'Find and book verified healthcare providers instantly',
  },
};

export default function Search() {
  const location = useLocation();
  const pathConfig = PATH_CONFIG[location.pathname] || null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    query: searchParams.get('q') || '',
    role: pathConfig?.filters?.role || searchParams.get('role') || 'all',
    specialization: searchParams.get('specialization') || 'all',
    service: pathConfig?.filters?.service || searchParams.get('service') || 'all',
    location: searchParams.get('location') || '',
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // On PATH_CONFIG pages (/doctors, /nurses, /home-visit, /book-appointment)
    // ignore query-param sync so pathname-based filters aren't overwritten
    if (pathConfig) return;

    // Sync filters from URL params (only for /search page)
    const newFilters = {
      query: searchParams.get('q') || '',
      role: searchParams.get('role') || 'all',
      specialization: searchParams.get('specialization') || 'all',
      service: searchParams.get('service') || 'all',
      location: searchParams.get('location') || '',
    };
    setFilters(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    fetchProviders();
  }, [filters.role, filters.specialization, filters.service, filters.location]);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.query) params.append('q', filters.query);
      if (filters.role && filters.role !== 'all') params.append('role', filters.role);
      if (filters.specialization && filters.specialization !== 'all') params.append('specialization', filters.specialization);
      if (filters.service && filters.service !== 'all') params.append('service', filters.service);
      if (filters.location) params.append('location', filters.location);
      // Request a larger page so listing pages show all providers (not just 10)
      params.append('limit', '100');
      
      const data = await apiRequest(`/search/providers?${params.toString()}`);
      setProviders(data.results || []);
    } catch (error) {
      toast.error('Failed to fetch providers');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProviders();
    
    // Update URL params
    const params = new URLSearchParams();
    if (filters.query) params.set('q', filters.query);
    if (filters.role !== 'all') params.set('role', filters.role);
    if (filters.specialization !== 'all') params.set('specialization', filters.specialization);
    if (filters.service !== 'all') params.set('service', filters.service);
    if (filters.location) params.set('location', filters.location);
    setSearchParams(params);
  };

  const getServiceIcons = (services, role) => {
    const icons = [];
    // When filtering by a specific service, show only that badge
    if (filters.service === 'clinic') {
      if (services?.includes('clinic')) icons.push({ icon: Building, label: 'Clinic Visit' });
      return icons;
    }
    if (filters.service === 'home_visit') {
      if (services?.includes('home_visit') || role === 'nurse') icons.push({ icon: Home, label: 'Home Visit' });
      return icons;
    }
    // Default mode: show only ONE primary service per card (prevent confusion)
    // Nurses always do home visits. For doctors: prefer clinic if available, else home.
    if (role === 'nurse') {
      icons.push({ icon: Home, label: 'Home Visit' });
    } else if (services?.includes('clinic')) {
      icons.push({ icon: Building, label: 'Clinic Visit' });
    } else if (services?.includes('home_visit')) {
      icons.push({ icon: Home, label: 'Home Visit' });
    }
    return icons;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="search-page">
      <SEO
        path={location.pathname}
        title={pathConfig?.title || "Find Doctors & Nurses Near You"}
        description={pathConfig?.description || "Search verified doctors, nurses, and clinics across India. Filter by specialization, location, and service type. Book instantly with transparent pricing on Physign."}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <span className="hidden sm:block text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Physign
              </span>
            </Link>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="flex-1 relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search doctors, specializations..."
                  value={filters.query}
                  onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                  className="pl-10 h-11 border-slate-200"
                  data-testid="search-input"
                />
              </div>
              <Button 
                type="submit" 
                className="h-11 px-6 bg-[#0F62FE] hover:bg-[#0050E6] text-white rounded-lg"
                data-testid="search-btn"
              >
                Search
              </Button>
            </form>

            <Button
              variant="outline"
              size="icon"
              className="shrink-0 lg:hidden"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Landing hero for SEO pages (/doctors, /nurses, /home-visit, /book-appointment) */}
      {pathConfig && (
        <section className="bg-gradient-to-b from-[#3B1C73]/5 to-transparent border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {pathConfig.heading}
            </h1>
            <p className="mt-3 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              {pathConfig.subheading}
            </p>
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Filters Sidebar */}
          <aside className={`${showFilters ? 'fixed inset-0 z-40 bg-white p-4' : 'hidden'} lg:block lg:relative lg:w-64 shrink-0`}>
            {showFilters && (
              <div className="flex items-center justify-between mb-4 lg:hidden">
                <h2 className="text-lg font-semibold">Filters</h2>
                <Button variant="ghost" size="icon" onClick={() => setShowFilters(false)}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </div>
            )}
            
            <Card className="lg:sticky lg:top-24">
              <CardContent className="p-4 space-y-4">
                <h3 className="font-semibold text-slate-900">Filters</h3>
                
                {/* Role Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Provider Type</label>
                  <Select 
                    value={filters.role} 
                    onValueChange={(value) => setFilters({ ...filters, role: value, specialization: 'all' })}
                  >
                    <SelectTrigger data-testid="role-filter">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Providers</SelectItem>
                      <SelectItem value="doctor">Doctors</SelectItem>
                      <SelectItem value="nurse">Nurses</SelectItem>
                      {/*<SelectItem value="lab">Labs</SelectItem>*/}
                    </SelectContent>
                  </Select>
                </div>

                {/* Specialization Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Specialization</label>
                  <Select 
                    value={filters.specialization} 
                    onValueChange={(value) => setFilters({ ...filters, specialization: value })}
                  >
                    <SelectTrigger data-testid="specialization-filter">
                      <SelectValue placeholder="Select specialization" />
                    </SelectTrigger>
                    <SelectContent>
                      {(filters.role === 'nurse' ? nurseSpecializations : doctorSpecializations).map((spec) => (
                        <SelectItem key={spec.value} value={spec.value}>
                          {spec.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Service Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Service Type</label>
                  <Select 
                    value={filters.service} 
                    onValueChange={(value) => setFilters({ ...filters, service: value })}
                  >
                    <SelectTrigger data-testid="service-filter">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((service) => (
                        <SelectItem key={service.value} value={service.value}>
                          {service.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Location</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Enter location"
                      value={filters.location}
                      onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                      className="pl-9"
                      data-testid="location-filter"
                    />
                  </div>
                </div>

                <Button 
                  className="w-full bg-[#0F62FE] hover:bg-[#0050E6] text-white"
                  onClick={handleSearch}
                  data-testid="apply-filters-btn"
                >
                  Apply Filters
                </Button>

                {showFilters && (
                  <Button 
                    variant="outline" 
                    className="w-full lg:hidden"
                    onClick={() => setShowFilters(false)}
                  >
                    Close
                  </Button>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* Results */}
          <main className="flex-1">
            <div className="mb-4 flex items-center justify-between">
              <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {loading ? 'Searching...' : `${providers.length} ${
                  filters.role === 'doctor' ? 'doctor' + (providers.length !== 1 ? 's' : '') :
                  filters.role === 'nurse' ? 'nurse' + (providers.length !== 1 ? 's' : '') :
                  filters.role === 'lab' ? 'lab' + (providers.length !== 1 ? 's' : '') :
                  'provider' + (providers.length !== 1 ? 's' : '')
                } found`}
              </h1>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#0F62FE]" />
              </div>
            ) : providers.length === 0 ? (
              <Card className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <SearchIcon className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No providers found</h3>
                <p className="text-slate-600 mb-4">Try adjusting your search filters</p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilters({ query: '', role: 'all', specialization: 'all', service: 'all', location: '' });
                    fetchProviders();
                  }}
                >
                  Clear Filters
                </Button>
              </Card>
            ) : (
              <div className="grid gap-4">
                {providers.map((provider, index) => (
                  <Card 
                    key={provider.id || index} 
                    className="provider-card overflow-hidden animate-fade-in"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`provider-card-${index}`}
                  >
                    <CardContent className="p-0">
                      <div className="flex flex-col sm:flex-row">
                        {/* Avatar Section */}
                        <div className="sm:w-48 p-4 flex sm:flex-col items-center gap-4 bg-slate-50 cursor-pointer" onClick={() => navigate(`/provider/${provider.user_id}`)}>
                          <div className="w-20 h-20 rounded-full bg-[#E0F2FE] flex items-center justify-center text-2xl font-bold text-[#0F62FE]" data-testid={`provider-avatar-${index}`}>
                            {provider.user?.avatar ? (
                              <img 
                                src={provider.user.avatar} 
                                alt={provider.user?.name} 
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              getInitials(provider.user?.name || 'Dr')
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                            <span className="font-medium">{provider.rating || '4.5'}</span>
                            <span className="text-slate-500 text-sm">({provider.total_reviews || 0})</span>
                          </div>
                        </div>

                        {/* Info Section */}
                        <div className="flex-1 p-4">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                            <div>
                              <h3 
                                className="text-lg font-semibold text-slate-900 hover:text-[#0F62FE] cursor-pointer transition-colors"
                                onClick={() => navigate(`/provider/${provider.user_id}`)}
                                data-testid={`provider-name-${index}`}
                              >
                                {provider.user?.name || 'Healthcare Provider'}
                              </h3>
                              {provider.approval_status === 'approved' && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium" data-testid={`verified-badge-${index}`}>
                                  <BadgeCheck className="w-3.5 h-3.5" />Verified
                                </span>
                              )}
                              <p className="text-[#0F62FE] font-medium capitalize">
                                {provider.specialization || provider.user?.role}
                              </p>
                              <p className="text-sm text-slate-600 mt-1">
                                {provider.qualification} • {provider.experience_years || '5'}+ years exp.
                              </p>
                            </div>
                            <Badge 
                              className={`self-start ${provider.user?.is_active !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-600'}`}
                              data-testid={`availability-badge-${index}`}
                            >
                              {provider.user?.is_active !== false ? 'Available' : 'Not Available'}
                            </Badge>
                          </div>

                          {/* Services */}
                          <div className="flex gap-2 mt-3">
                            {getServiceIcons(provider.services, provider.user?.role).map((service, i) => (
                              <div 
                                key={i}
                                className="flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-sm"
                              >
                                <service.icon className="w-4 h-4" />
                                <span>{service.label}</span>
                              </div>
                            ))}
                          </div>

                          {/* Location, Clinic & Fee */}
                          <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
                            {provider.clinic_name && (
                              <div className="flex items-center gap-1">
                                <Building className="w-4 h-4 text-blue-500" />
                                <span className="font-medium">{provider.clinic_name}</span>
                              </div>
                            )}
                            {provider.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span>{provider.location}</span>
                              </div>
                            )}
                            {provider.user?.is_active !== false && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>Available today</span>
                            </div>
                            )}
                          </div>

                          {/* Fee & Book Button */}
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 pt-4 border-t border-slate-100 gap-3">
                            <div className="flex flex-wrap items-baseline gap-3">
                              {filters.service === 'clinic' ? (
                                provider.user?.role !== 'nurse' && provider.services?.includes('clinic') && (
                                  <div>
                                    <span className="text-2xl font-bold text-slate-900">₹{provider.consultation_fee || 500}</span>
                                    <span className="text-slate-500 text-sm ml-1">consultation</span>
                                  </div>
                                )
                              ) : filters.service === 'home_visit' ? (
                                (provider.services?.includes('home_visit') || provider.user?.role === 'nurse') && (
                                  <div>
                                    <span className="text-2xl font-bold text-slate-900">₹{provider.home_visit_fee || 1000}</span>
                                    <span className="text-slate-500 text-sm ml-1">home visit</span>
                                  </div>
                                )
                              ) : (
                                <>
                                  {/* Default mode: show only ONE primary service to avoid confusion */}
                                  {provider.user?.role === 'nurse' ? (
                                    <div>
                                      <span className="text-2xl font-bold text-slate-900">₹{provider.home_visit_fee || 1000}</span>
                                      <span className="text-slate-500 text-sm ml-1">home visit</span>
                                    </div>
                                  ) : provider.services?.includes('clinic') ? (
                                    <div>
                                      <span className="text-2xl font-bold text-slate-900">₹{provider.consultation_fee || 500}</span>
                                      <span className="text-slate-500 text-sm ml-1">consultation</span>
                                    </div>
                                  ) : provider.services?.includes('home_visit') ? (
                                    <div>
                                      <span className="text-2xl font-bold text-slate-900">₹{provider.home_visit_fee || 1000}</span>
                                      <span className="text-slate-500 text-sm ml-1">home visit</span>
                                    </div>
                                  ) : (
                                    <div>
                                      <span className="text-2xl font-bold text-slate-900">₹{provider.consultation_fee || 500}</span>
                                      <span className="text-slate-500 text-sm ml-1">consultation fee</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                              <ShareProfileDialog
                                providerId={provider.user_id}
                                name={provider.user?.name}
                                specialization={provider.specialization}
                                role={provider.user?.role}
                                triggerClassName="flex-1 sm:flex-initial"
                                testId={`share-btn-${index}`}
                              />
                              <Button 
                                variant="outline"
                                className="flex-1 sm:flex-initial rounded-lg"
                                onClick={() => navigate(`/provider/${provider.user_id}`)}
                                data-testid={`view-profile-btn-${index}`}
                              >
                                <User className="w-4 h-4 mr-2" />
                                View Profile
                              </Button>
                              <Button 
                                className={`flex-1 sm:flex-initial rounded-lg ${provider.user?.is_active !== false ? 'bg-[#0F62FE] hover:bg-[#0050E6] text-white' : 'bg-slate-200 text-slate-500 cursor-not-allowed'}`}
                                onClick={() => provider.user?.is_active !== false && navigate(`/book/${provider.user_id}`)}
                                disabled={provider.user?.is_active === false}
                                data-testid={`book-btn-${index}`}
                              >
                                <Calendar className="w-4 h-4 mr-2" />
                                {provider.user?.is_active !== false ? 'Book' : 'Unavailable'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* SEO Content Section — only for landing routes (/doctors, /nurses, /home-visit, /book-appointment) */}
            {pathConfig && SEO_CONTENT[location.pathname] && (
              <section className="mt-16 space-y-12" data-testid="seo-content-section">
                {/* Long-form intro */}
                <div className="max-w-4xl">
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    About {pathConfig.heading}
                  </h2>
                  <div className="prose prose-slate max-w-none space-y-4">
                    <p className="text-slate-700 leading-relaxed">{SEO_CONTENT[location.pathname].intro}</p>
                    <p className="text-slate-700 leading-relaxed">{SEO_CONTENT[location.pathname].body}</p>
                  </div>
                </div>

                {/* Popular Specializations / Services */}
                {SEO_CONTENT[location.pathname].specializations?.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                      Popular {location.pathname === '/nurses' ? 'Nurse Services' : location.pathname === '/home-visit' ? 'Home Visit Services' : 'Specializations'}
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {SEO_CONTENT[location.pathname].specializations.map((spec) => (
                        <div key={spec} className="p-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:border-[#3B1C73] hover:text-[#3B1C73] transition-colors cursor-default">
                          {spec}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Popular Cities */}
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    Available Across India
                  </h2>
                  <p className="text-slate-600 mb-4 text-sm">Book verified {pathConfig.heading.toLowerCase()} in these cities and more:</p>
                  <div className="flex flex-wrap gap-2">
                    {POPULAR_CITIES.map((city) => (
                      <span key={city} className="px-3 py-1.5 bg-white border border-slate-200 rounded-full text-sm text-slate-700">
                        {city}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Trust bar */}
                <div className="p-6 bg-gradient-to-br from-[#3B1C73]/5 to-purple-50 rounded-2xl border border-purple-100">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>Why choose Physign</h2>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div className="flex gap-3"><span className="text-emerald-600 font-bold">✓</span><span><strong>Verified Professionals</strong> — Every doctor and nurse is manually verified with medical council registration, degrees, ID and experience checks.</span></div>
                    <div className="flex gap-3"><span className="text-emerald-600 font-bold">✓</span><span><strong>Transparent Pricing</strong> — See fees upfront before booking. No hidden charges.</span></div>
                    <div className="flex gap-3"><span className="text-emerald-600 font-bold">✓</span><span><strong>60-Second Booking</strong> — Pick a slot, confirm, and get instant WhatsApp/SMS confirmation.</span></div>
                    <div className="flex gap-3"><span className="text-emerald-600 font-bold">✓</span><span><strong>Genuine Reviews</strong> — Ratings and reviews from real patients help you choose.</span></div>
                    <div className="flex gap-3"><span className="text-emerald-600 font-bold">✓</span><span><strong>Home Visits Available</strong> — Doctors and nurses that come to your doorstep.</span></div>
                    <div className="flex gap-3"><span className="text-emerald-600 font-bold">✓</span><span><strong>Easy Cancellation</strong> — Cancel or reschedule up to 2 hours before, free of charge.</span></div>
                  </div>
                </div>

                {/* FAQs */}
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>Frequently Asked Questions</h2>
                  <div className="space-y-3">
                    {SEO_CONTENT[location.pathname].faqs.map((faq, i) => (
                      <details key={i} className="p-4 bg-white border border-slate-200 rounded-lg group" data-testid={`seo-faq-${i}`}>
                        <summary className="font-semibold text-slate-900 cursor-pointer flex items-center justify-between">
                          {faq.q}
                          <span className="text-[#3B1C73] group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="mt-3 text-slate-700 text-sm leading-relaxed">{faq.a}</p>
                      </details>
                    ))}
                  </div>
                </div>

                {/* FAQPage JSON-LD structured data — helps Google understand FAQ content */}
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                      '@context': 'https://schema.org',
                      '@type': 'FAQPage',
                      mainEntity: SEO_CONTENT[location.pathname].faqs.map((f) => ({
                        '@type': 'Question',
                        name: f.q,
                        acceptedAnswer: { '@type': 'Answer', text: f.a },
                      })),
                    }),
                  }}
                />
              </section>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
